import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const CreateCurrency: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('currencies');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    symbol: "",
    hedge: 0,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'hedge' ? Number(value) : value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = t('currencies:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('currencies:validation.nameMinLength');
    }

    if (!formData.symbol.trim()) {
      newErrors.symbol = t('currencies:validation.symbolRequired');
    }

    if (formData.hedge === null || formData.hedge === undefined || formData.hedge < 0) {
      newErrors.hedge = t('currencies:validation.hedgeRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitItem = async () => {
    // Validate form before submission
    if (!validateForm()) {
      showErrorToast(t('currencies:validation.fillAllFields'));
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/currency', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('messages.createError'));
        return;
      }

      showSuccessToast(t('messages.createSuccess'));
      setFormData({ name: "", symbol: "", hedge: 0 }); // Reset form
    } catch (error: any) {
      showErrorToast(t('messages.createError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('breadcrumbs.currencies'), href: '/currencies' },
    { label: t('breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('createCurrency')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}

    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-md-6">
            <MultiLanguageTextInput
              label={t('fields.name')}
              value={formData.name}
              onChange={(value) => {
                setFormData({ ...formData, name: value });
                if (errors.name) {
                  setErrors(prev => ({ ...prev, name: undefined }));
                }
              }}
              placeholder={t('placeholders.name')}
              error={errors.name}
              disabled={loading}
              required
            />
          </div>

          <div className="col-md-6">
            <MultiLanguageTextInput
              label={t('fields.symbol')}
              value={formData.symbol}
              onChange={(value) => {
                setFormData({ ...formData, symbol: value });
                if (errors.symbol) {
                  setErrors(prev => ({ ...prev, symbol: undefined }));
                }
              }}
              placeholder={t('placeholders.symbol')}
              error={errors.symbol}
              disabled={loading}
              required
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.hedge')} <span className="required">*</span>
            </label>
            <input
              type="number"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.hedge ? 'is-invalid' : ''}`}
              name="hedge"
              step={0.001}
              value={formData.hedge}
              onChange={handleInputChange}
              placeholder={t('placeholders.hedge')}
              dir={isRTL ? 'rtl' : 'ltr'}
              min="0"
              required
            />
            {errors.hedge && <div className="invalid-feedback">{errors.hedge}</div>}
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton
            variant="secondary"
            onClick={() => navigate('/currencies')}
            className={isRTL ? 'ms-2' : 'me-2'}
          >
            <i className={`fa ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'} ${isRTL ? 'ms-1' : 'me-1'}`}></i>
            {t('actions.back')}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={submitItem}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('actions.saving') : t('actions.save')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
